import type { ValidationStageKey } from '@ispot/shared'

const RADAR_VIEW_W = 540
const RADAR_VIEW_H = 540
const CENTER_X = 270
const CENTER_Y = 265
const RADIUS = 210

const stageAngle = (index: number) => ((index * 72 - 90) * Math.PI) / 180

const stagePoint = (index: number, levelFraction: number) => {
  const a = stageAngle(index)
  const r = levelFraction * RADIUS
  return { x: CENTER_X + r * Math.cos(a), y: CENTER_Y + r * Math.sin(a) }
}

const polygonPoints = (level: number, count: number) =>
  Array.from({ length: count }, (_, i) => {
    const p = stagePoint(i, level)
    return `${p.x},${p.y}`
  }).join(' ')

const LABEL_OFFSETS = [
  { dx: 0, dy: -30, anchor: 'middle' as const },
  { dx: 20, dy: -4, anchor: 'start' as const },
  { dx: 14, dy: 30, anchor: 'start' as const },
  { dx: -14, dy: 30, anchor: 'end' as const },
  { dx: -20, dy: -4, anchor: 'end' as const },
]

interface RadarStage {
  key: ValidationStageKey
  shortName: string
}

interface RadarTone {
  stroke: string
  fill: string
}

interface ValidationRadarProps {
  stages: RadarStage[]
  scores: Record<ValidationStageKey, number>
  selectedStage: ValidationStageKey | null
  onSelectStage: (key: ValidationStageKey) => void
  tone: RadarTone
}

export function ValidationRadar({
  stages,
  scores,
  selectedStage,
  onSelectStage,
  tone,
}: ValidationRadarProps) {
  const dataPoints = stages.map((stage, i) => {
    const score = scores[stage.key] ?? 0
    return stagePoint(i, score / 100)
  })

  const dataPolygon = dataPoints.map((p) => `${p.x},${p.y}`).join(' ')

  return (
    <div className="grid place-items-center">
      <svg
        viewBox={`0 0 ${RADAR_VIEW_W} ${RADAR_VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-auto block"
        style={{ overflow: 'visible' }}
      >
        <g fill="none" stroke="var(--color-border)" strokeWidth={1}>
          <polygon points={polygonPoints(0.25, stages.length)} />
          <polygon points={polygonPoints(0.75, stages.length)} />
          <polygon points={polygonPoints(1, stages.length)} />
        </g>

        <g stroke="var(--color-border)" strokeWidth={1}>
          {stages.map((_, i) => {
            const p = stagePoint(i, 1)
            return <line key={i} x1={CENTER_X} y1={CENTER_Y} x2={p.x} y2={p.y} />
          })}
        </g>

        {[0.25, 0.75].map((lvl) => {
          const y = CENTER_Y - lvl * RADIUS + 4
          return (
            <text
              key={lvl}
              x={CENTER_X + 6}
              y={y}
              textAnchor="start"
              fill="var(--color-fg-muted)"
              opacity={0.5}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                letterSpacing: '0.04em',
              }}
            >
              {lvl * 100}
            </text>
          )
        })}

        <polygon
          points={dataPolygon}
          fill={tone.fill}
          stroke={tone.stroke}
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {selectedStage &&
          dataPoints.map((p, i) => {
            if (stages[i].key !== selectedStage) return null
            return (
              <circle
                key={`ring-${i}`}
                cx={p.x}
                cy={p.y}
                r={13}
                fill="none"
                stroke={tone.stroke}
                strokeWidth={1.5}
                opacity={0.28}
              />
            )
          })}

        {dataPoints.map((p, i) => {
          const stage = stages[i]
          const isSelected = stage.key === selectedStage
          return (
            <circle
              key={stage.key}
              cx={p.x}
              cy={p.y}
              r={isSelected ? 7 : 5.5}
              fill={isSelected ? tone.stroke : 'var(--color-bg-card)'}
              stroke={tone.stroke}
              strokeWidth={2}
              className="cursor-pointer transition-[r] duration-[var(--dur-normal)]"
              onClick={() => onSelectStage(stage.key)}
            />
          )
        })}

        {stages.map((stage, i) => {
          const outer = stagePoint(i, 1)
          const offset = LABEL_OFFSETS[i] ?? LABEL_OFFSETS[0]
          const isSelected = stage.key === selectedStage
          const score = scores[stage.key] ?? 0
          const labelX = outer.x + offset.dx
          const nameY = outer.y + offset.dy
          const scoreY = nameY + 22
          return (
            <g
              key={stage.key}
              onClick={() => onSelectStage(stage.key)}
              className="cursor-pointer"
            >
              <text
                x={labelX}
                y={nameY}
                textAnchor={offset.anchor}
                fill="var(--color-fg)"
                opacity={isSelected ? 1 : 0.85}
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 20,
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                }}
              >
                {stage.shortName}
              </text>
              <text
                x={labelX}
                y={scoreY}
                textAnchor={offset.anchor}
                fill={isSelected ? 'var(--color-fg)' : 'var(--color-fg-muted)'}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 18,
                  fontWeight: isSelected ? 600 : 400,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.02em',
                }}
              >
                {score}%
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
